import { ReactNode } from 'react';
import { Trans } from '@lingui/react/macro';
import { useFieldContext } from '@/lib/form';
import { Label } from '@/common/components/ui/label';
import { FormFieldErrors } from '@/common/components/form-field-errors/FormFieldErrors';
import { useShouldShowFieldErrors } from '@/common/hooks/useShouldShowFieldErrors';
import { LocationPicker } from '@/features/geolocation/components/location-picker/LocationPicker';
import type { AddressInput, LocationValue } from '@/features/geolocation/types/location';

export interface FormLocationFieldProps {
  label?: ReactNode;
  description?: ReactNode;
  /** Address the picker geocodes; comes from the sibling address fields. */
  address: AddressInput;
  /** Nonce; the host increments it on address-field blur. */
  locateSignal: number;
  disabled?: boolean;
}

/**
 * Bridges `LocationPicker` to TanStack Form.
 *
 * Deliberately NOT registered in `src/lib/form.ts` — registering it would drag
 * leaflet into the module graph of every form in the app. Host forms render it
 * explicitly inside `<form.AppField name="location">`.
 */
export const FormLocationField = ({
  label,
  description,
  address,
  locateSignal,
  disabled,
}: FormLocationFieldProps) => {
  const field = useFieldContext<LocationValue>();
  const { shouldShowFieldErrors } = useShouldShowFieldErrors();

  return (
    <div className="flex flex-col gap-1.5">
      {label && <Label>{label}</Label>}
      {description && <p className="text-sm text-muted-foreground">{description}</p>}

      <LocationPicker
        value={field.state.value}
        onChange={field.handleChange}
        address={address}
        locateSignal={locateSignal}
        disabled={disabled}
      />

      {shouldShowFieldErrors && (
        <div className="text-sm text-destructive">
          <FormFieldErrors />
        </div>
      )}

      <p className="sr-only">
        <Trans>The position is optional. Without it, production prediction is less accurate.</Trans>
      </p>
    </div>
  );
};
